import { useState, useEffect } from "react"
import Spinner from '../Spinner/Spinner'
import mainservice from "../../services/MainService"
import DatePicker from 'react-datepicker'
import "react-datepicker/dist/react-datepicker.css"
import './Preferences.scss'
import { PreferenceType, PreferenceValue } from "../../DTO/PreferencesConfig"
import PreferenceService, { PreferenceDTO } from "../../DTO/PreferencesDTO"

import AddIcon from '@mui/icons-material/Add';
import { Alert, Box, Button, Input, Stack } from "@mui/material"
import InputB from "../InputB/InputB"

enum Status {
  loading,
  ready
}

type SaveFeedback = 'idle' | 'saving' | 'saved' | 'error'

const SaveControls = (props: {
  dirty: boolean
  feedback: SaveFeedback
  onSave: () => void
}) => {
  if (!props.dirty && props.feedback !== 'saved' && props.feedback !== 'error') {
    return null
  }
  return <Stack direction="row" spacing={1} alignItems="center" sx={{ mt: 1 }}>
    {
      props.dirty &&
      <Button
        variant="contained"
        disabled={props.feedback === 'saving'}
        onClick={props.onSave}
      >{props.feedback === 'saving' ? 'Speichert…' : 'Speichern'}</Button>
    }
    {
      props.feedback === 'saved' &&
      <Alert severity="success" sx={{ py: 0 }}>Gespeichert</Alert>
    }
    {
      props.feedback === 'error' &&
      <Alert severity="error" sx={{ py: 0 }}>Speichern fehlgeschlagen</Alert>
    }
  </Stack>
}

const RenderValueInput = (props: {
  value: PreferenceValue,
  preference: PreferenceDTO,
  reRenderParent: () => void,
  showDates?: boolean,
}) => {
  const p = props.preference
  const v = props.value
  const [rerender, setRerender] = useState(0)
  const withDates = props.showDates === true
  const initialValue = v.value1 || ''
  const [value, setValue] = useState(initialValue)
  const [savedValue, setSavedValue] = useState(initialValue)
  const [feedback, setFeedback] = useState<SaveFeedback>('idle')
  useEffect(() => {
    const next = v.value1 || ''
    setValue(next)
    setSavedValue(next)
    setFeedback('idle')
  }, [v.id, v.value1])
  const dirty = value !== savedValue
  const save = async () => {
    setFeedback('saving')
    try {
      await p.setValue(v.id || -1, value)
      setSavedValue(value)
      setFeedback('saved')
      props.reRenderParent()
      setRerender(rerender + 1)
      window.setTimeout(() => setFeedback(current => current === 'saved' ? 'idle' : current), 2500)
    } catch {
      setFeedback('error')
    }
  }
  let className = 'w3-col s1'
  if (p.type === PreferenceType.text || p.type === PreferenceType.code) {
    className = 'w3-col s12'
  }
  const showButton = () => {
    switch(p.type) {
      case PreferenceType.boolean:
        if (value === '1') {
          return <Button
            variant="contained"
            onClick={async () => {
              const newValue = await p.setValue(v.id  || -1, '0')
              setValue(newValue)
              setSavedValue(newValue)
            }}
          >Ja</Button>
        } else {
          return <Button
            variant="outlined"
            onClick={async () => {
              const newValue = await p.setValue(v.id || -1, '1')
              setValue(newValue)
              setSavedValue(newValue)
            }}
          >Nein</Button>
        }
      case PreferenceType.text:
        return <InputB
          className='w100'
          value={value || ''}
          returnVal={ async (next: string) => {
            await p.setValue(v.id || -1, next)
            setValue(next)
            setSavedValue(next)
            setRerender(rerender + 1)
          } }
          type='wysiwyg'
        />
      case PreferenceType.code:
        return <Box>
            <textarea
              className='w3-input w3-border'
              value={value || ''}
              onChange={(event) => {
                setValue(event.target.value)
                if (feedback !== 'idle') { setFeedback('idle') }
              }}
            />
            <SaveControls dirty={dirty} feedback={feedback} onSave={save} />
          </Box>
        case PreferenceType.line:
          return <Box>
            <Input
              className='w100'
              disableUnderline
              sx={{ border: '1px solid grey', borderRadius: '10px', px: 1, width: '100%' }}
              value={value || ''}
              onChange={(event) => {
                setValue(event.target.value)
                if (feedback !== 'idle') { setFeedback('idle') }
              }}
            />
            <SaveControls dirty={dirty} feedback={feedback} onSave={save} />
          </Box>
      default:
        return <div>Typ noch nicht definiert!</div>
    }
  }
  if (withDates) {
    return <div className='preference-item-value-item w3-row w3-center w3-card w3-margin w3-padding'>
      <div
        className='w3-col s1'
      >
        <button
          className='w3-show-inline-block w3-red w3-button'
          onClick={async () => {
            await p.removeValue(v.id || -1)
            props.reRenderParent()
          }}
        >X</button>
      </div>
      <div className='w3-col s2 w3-display-container'>
        <DatePicker
          selected={v.start || null}
          onChange={async (date) => {
            await p.saveDate(v.id || -1, date, 'start')
            props.reRenderParent()
          }}
          onBlur={async (date) => {
            await p.saveValue(v.id || -1)
            props.reRenderParent()
          }}
        />
      </div>
      <div className='w3-col s2 w3-display-container'>
        <DatePicker
          selected={v.end || null}
          onChange={async (date) => {
            await p.saveDate(v.id || -1, date, 'end')
            props.reRenderParent()
          }}
          onBlur={async () => {
            await p.saveValue(v.id || -1)
            props.reRenderParent()
          }}
        />
      </div>
      <div className='w3-col s1 w3-display-container'>
        <button
          className='w3-show-inline-block w3-yellow w3-button'
          title='Enddatum entfernen (open end)'
          onClick={async () => {
            await p.saveDate(v.id || -1, null, 'end')
            props.reRenderParent()
          }}
        >&times;</button>
      </div>
      <div className={className}>
        { showButton() }
      </div>
    </div>
  }
  return <Box>
    { showButton() }
  </Box>
}

const RenderPreference = (props: {
  preference: PreferenceDTO
}) => {
  const p = props.preference
  const [renderCounter, setRenderCounter] = useState(0)
  const allowTimed = p.key1 !== 'print'
  return <Stack spacing={1}>
    <h2>{p.name}</h2>
    {
      p.description &&
      <Alert
        severity="info"
        >
        <div
          dangerouslySetInnerHTML={{__html: p.description}}
        ></div>
      </Alert>
    }
    {
      allowTimed &&
      <Button
        className='w3-right'
        onClick={() => {
          p.initNewValue()
          setRenderCounter(renderCounter + 1)
        }}
      ><AddIcon /></Button>
    }
    <RenderValueInput
      value={p.getDefaultValue()}
      preference={p}
      reRenderParent={() => setRenderCounter(renderCounter + 1)}
    />
    {
      allowTimed &&
      p.getValues().map((v, i) => <RenderValueInput
        key={`${i}-value-id-${v.id}`}
        value={v}
        preference={p}
        showDates={true}
        reRenderParent={() => setRenderCounter(renderCounter + 1)}
      />)
    }
    
  </Stack>
}

export default function Preferences(props: {
  version?: 'workbook' | 'core',
}) {
  const [status, setStatus] = useState(Status.loading)
  const [page, setPage] = useState(0)
  useEffect(() => {
    PreferenceService.init().then(() => {
      setStatus(Status.ready)
    })
  }, [])
  const RenderContent = () => {
    switch(status) {
      case Status.loading:
        return <Spinner />
      case Status.ready:
        return <>
        <div className="flex flex-row w3-border">
          {
            PreferenceService.listCategories(props.version).map((c, i) => <button
              key={i}
              className={`w3-button ${page === i ? 'w3-blue' : ''}`}
              onClick={() => setPage(i)}
            >{c}</button>)
          }
        </div>
        <Stack spacing={4}>
          {
            PreferenceService.list(page, props.version).map(p => <RenderPreference
              key={`pref-${p.key1}-${p.key2}`}
              preference={p}
            />)
          }
        </Stack>
        </>
        
    }
  }
  return <div className='w3-container scrollY h100'>
    {
      props.version !== 'core' &&
      <button className='secondary-button' onClick={() => mainservice.navTo([['view', 'adminlogin']])}>&larr; Dashboard</button>
    }
    <h1>Einstellungen</h1>
    { RenderContent() }
  </div>
}
